import React, { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../hooks/ThemeContext';
import { CartContext } from '../../hooks/CartContext';
import SectionTitle from '../../components/common/SectionTitle';
import ProductCard from '../../components/common/ProductCard';
import CTABox from '../../components/common/CTABox';
import LoadingSpinner from '../../components/globals/LoadingSpinner';
import { useProducts } from '../../hooks/useProducts';

const Shop = () => {
  const { theme } = useContext(ThemeContext);
  const { addToCart } = useContext(CartContext);
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const { products, loading, error } = useProducts();
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = useMemo(() => {
    const keys = Array.from(new Set(products.map((p) => (p.category || 'autre').toLowerCase())));
    const dynamic = keys.map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) }));
    return [
      { key: 'all', label: 'Tout' },
      ...dynamic,
    ];
  }, [products]);

  // Filtrage des produits
  const filteredProducts = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter(p => (p.category || '').toLowerCase() === activeCategory);
  }, [activeCategory, products]);

  const handleAddToCart = async (productId) => {
    const success = await addToCart(productId, 1);
    if (success) {
      // Afficher un message de succès
    }
  };

  const handleViewDetails = (productId) => {
    if (productId) {
      navigate(`/shop/${productId}`);
    }
  };

  const getMeta = (product) => {
    if (product.sale_type === 'unique') return 'Exemplaire unique';
    const stock = product.stock ?? product.available_stock ?? 0;
    return `Stock: ${stock}`;
  };

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        
        {/* En-tête */}
        <SectionTitle 
          title="Boutique de Créations Digitales"
          subtitle="Bannières, logos, images et overlays personnalisés pour votre marque"
          align="center"
        />

        {error && (
          <div className="text-center text-red-500 mb-4">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Filtres */}
            {categories.length > 1 && (
              <nav className="flex flex-wrap justify-center gap-3 mb-8" aria-label="Filtres produits">
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className={`px-6 py-2 rounded-md font-medium transition-all border ${
                      activeCategory === cat.key
                        ? isDark
                          ? 'bg-rose-600 text-white border-rose-600'
                          : 'bg-rose-600 text-white border-rose-600'
                        : isDark
                        ? 'bg-[#0b0b0c] text-white border-white/10 hover:border-rose-500/40'
                        : 'bg-white/95 text-gray-800 border-gray-200 hover:border-rose-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </nav>
            )}

            {/* Grille de produits */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-sm opacity-80">
                Aucun produit disponible pour le moment.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {filteredProducts.map(product => (
                  <div key={product.id_product ?? product.id} className="relative">
                    <ProductCard
                      title={product.name_product || product.name}
                      description={product.description_product || product.description}
                      price={product.price_product || product.price}
                      image={product.image}
                      category={product.category}
                      meta={getMeta(product)}
                      onAddToCart={() => handleAddToCart(product.id_product ?? product.id)}
                      onViewDetails={() => handleViewDetails(product.id_product ?? product.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* CTA Section */}
        <div className="mt-16">
          <CTABox
            title="Besoin d'une création sur mesure ?"
            description="Notre équipe peut créer des designs personnalisés adaptés à vos besoins spécifiques"
            buttonText="Demander un devis"
            buttonLink="/contact"
            variant="primary"
          />
        </div>

        {/* Informations supplémentaires */}
        <div className={`mt-12 grid grid-cols-1 md:grid-cols-3 gap-6`}>
          <div className={`p-6 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
            <div className="text-3xl mb-2">🎨</div>
            <h3 className="text-lg font-semibold mb-2">Design Unique</h3>
            <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Chaque création est réalisée sur mesure selon vos besoins
            </p>
          </div>
          <div className={`p-6 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
            <div className="text-3xl mb-2">⚡</div>
            <h3 className="text-lg font-semibold mb-2">Livraison Rapide</h3>
            <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Délai moyen de 3-5 jours ouvrés pour vos créations
            </p>
          </div>
          <div className={`p-6 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
            <div className="text-3xl mb-2">🔄</div>
            <h3 className="text-lg font-semibold mb-2">Révisions Incluses</h3>
            <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Jusqu'à 3 révisions pour garantir votre satisfaction
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Shop;
